import { LABEL_POSITIONS, LABEL_VARIANTS, LABEL_TEXT_ALIGNS, POINT_FACES } from '@examdojo/core/widget';
import type { StyleSectionDescriptor } from '@examdojo/core/widget';
import type { FunctionMeta } from '../elements/functions/function.model';
import { FunctionRegistry } from '../elements/functions/function.registry';
import { FeatureRegistry } from '../elements/features/feature.registry';
import type {
  FeatureType,
  FunctionType,
  LabelElementParams,
  LineElementParams,
  PointElementParams,
  PolygonElementParams,
  PrimitiveType,
} from '../graphpad.model';

import { LINEAR_META } from '../elements/functions/linear';
import { QUADRATIC_META } from '../elements/functions/quadratic';
import { QUADRATIC_VERTEX_META } from '../elements/functions/quadratic-vertex';
import { CUBIC_META } from '../elements/functions/cubic';
import { CUBIC_GENERAL_META } from '../elements/functions/cubic-general';
import { SINE_META } from '../elements/functions/sine';
import { COSINE_META } from '../elements/functions/cosine';
import { EXPONENTIAL_META } from '../elements/functions/exponential';
import { LOGARITHMIC_META } from '../elements/functions/logarithmic';
import { ABSOLUTE_META } from '../elements/functions/absolute';
import { SQRT_META } from '../elements/functions/sqrt';
import { TANGENT_FN_META } from '../elements/functions/tangent-fn';

import { TANGENT_DEFAULT_PARAMS } from '../elements/features/tangent';
import { SECANT_DEFAULT_PARAMS } from '../elements/features/secant';
import { DERIVATIVE_DEFAULT_PARAMS } from '../elements/features/derivative';
import { DOMAIN_DEFAULT_PARAMS } from '../elements/features/domain';
import { RANGE_DEFAULT_PARAMS } from '../elements/features/range';
import { HANDLE_DEFAULT_PARAMS } from '../elements/features/handle';
import { TRAJECTORY_DEFAULT_PARAMS } from '../elements/features/trajectory';
import { FUNCTION_AREA_DEFAULT_PARAMS } from '../elements/features/function-area';
import { INTERSECTION_AREA_DEFAULT_PARAMS } from '../elements/features/intersection-area';
import { AXIS_OF_SYMMETRY_DEFAULT_PARAMS } from '../elements/features/axis-of-symmetry';
import { ASYMPTOTES_DEFAULT_PARAMS } from '../elements/features/asymptotes';
import { ROOTS_DEFAULT_PARAMS } from '../elements/features/roots';
import { EXTREMA_DEFAULT_PARAMS } from '../elements/features/extrema';
import { Y_INTERCEPT_DEFAULT_PARAMS } from '../elements/features/y-intercept';
import { INFLECTION_DEFAULT_PARAMS } from '../elements/features/inflection';
import { INTERSECTION_DEFAULT_PARAMS } from '../elements/features/intersection';
import { MONOTONICITY_DEFAULT_PARAMS } from '../elements/features/monotonicity';
import { AMPLITUDE_DEFAULT_PARAMS } from '../elements/features/amplitude';
import { WAVELENGTH_DEFAULT_PARAMS } from '../elements/features/wavelength';
import { PERIOD_DEFAULT_PARAMS } from '../elements/features/period';
import { PHASE_SHIFT_DEFAULT_PARAMS } from '../elements/features/phase-shift';

// =============================================================================
// FUNCTION META MAP
// =============================================================================

/**
 * Loosened FunctionMeta for the editor — only uses defaultParams/defaultRanges
 * as Record<string, number>, losing per-function param name type safety.
 */
export interface EditorFunctionMeta {
  readonly availableFeatures: readonly FeatureType[];
  readonly defaultParams: Record<string, number>;
  readonly defaultRanges: Record<string, readonly [number, number]>;
}

function toEditorMeta(meta: FunctionMeta<object>): EditorFunctionMeta {
  return {
    availableFeatures: meta.availableFeatures,
    defaultParams: meta.defaultParams as Record<string, number>,
    defaultRanges: meta.defaultRanges as unknown as Record<string, readonly [number, number]>,
  };
}

const FUNCTION_META_MAP: Record<string, EditorFunctionMeta> = {
  linear: toEditorMeta(LINEAR_META),
  quadratic: toEditorMeta(QUADRATIC_META),
  'quadratic-vertex': toEditorMeta(QUADRATIC_VERTEX_META),
  cubic: toEditorMeta(CUBIC_META),
  'cubic-general': toEditorMeta(CUBIC_GENERAL_META),
  sine: toEditorMeta(SINE_META),
  cosine: toEditorMeta(COSINE_META),
  exponential: toEditorMeta(EXPONENTIAL_META),
  logarithmic: toEditorMeta(LOGARITHMIC_META),
  absolute: toEditorMeta(ABSOLUTE_META),
  sqrt: toEditorMeta(SQRT_META),
  'tangent-fn': toEditorMeta(TANGENT_FN_META),
};

export function getFunctionMeta(type: string): EditorFunctionMeta | undefined {
  return FUNCTION_META_MAP[type];
}

export function getFunctionTypes(): FunctionType[] {
  return FunctionRegistry.getTypes() as FunctionType[];
}

export function getFeatureTypes(): FeatureType[] {
  return FeatureRegistry.getTypes() as FeatureType[];
}

export function getAvailableFeatures(functionType: string): readonly FeatureType[] {
  const meta = getFunctionMeta(functionType);
  return meta?.availableFeatures ?? [];
}

// =============================================================================
// FEATURE DEFAULT PARAMS MAP
// =============================================================================

/**
 * Maps feature type → default params (excluding `of`), sourced from each
 * feature's actual *_DEFAULT_PARAMS export. Empty object = no extra params.
 */
const FEATURE_DEFAULT_PARAMS_MAP: Record<string, Record<string, unknown>> = {
  tangent: { ...TANGENT_DEFAULT_PARAMS },
  secant: { ...SECANT_DEFAULT_PARAMS },
  derivative: { ...DERIVATIVE_DEFAULT_PARAMS },
  domain: { ...DOMAIN_DEFAULT_PARAMS },
  range: { ...RANGE_DEFAULT_PARAMS },
  handle: { ...HANDLE_DEFAULT_PARAMS },
  trajectory: { ...TRAJECTORY_DEFAULT_PARAMS },
  functionArea: { ...FUNCTION_AREA_DEFAULT_PARAMS },
  intersectionArea: { ...INTERSECTION_AREA_DEFAULT_PARAMS },
  axisOfSymmetry: { ...AXIS_OF_SYMMETRY_DEFAULT_PARAMS },
  asymptotes: { ...ASYMPTOTES_DEFAULT_PARAMS },
  roots: { ...ROOTS_DEFAULT_PARAMS },
  extrema: { ...EXTREMA_DEFAULT_PARAMS },
  yIntercept: { ...Y_INTERCEPT_DEFAULT_PARAMS },
  inflection: { ...INFLECTION_DEFAULT_PARAMS },
  intersection: { ...INTERSECTION_DEFAULT_PARAMS },
  monotonicity: { ...MONOTONICITY_DEFAULT_PARAMS },
  amplitude: { ...AMPLITUDE_DEFAULT_PARAMS },
  wavelength: { ...WAVELENGTH_DEFAULT_PARAMS },
  period: { ...PERIOD_DEFAULT_PARAMS },
  phaseShift: { ...PHASE_SHIFT_DEFAULT_PARAMS },
};

export function getFeatureDefaultParams(type: string): Record<string, unknown> {
  return FEATURE_DEFAULT_PARAMS_MAP[type] ?? {};
}

/** Features that require two parent references (intersection types) */
export const DUAL_PARENT_FEATURES: FeatureType[] = ['intersection', 'intersectionArea'];

// =============================================================================
// DISPLAY STYLE KEY MAPPING
// =============================================================================

/** Which FeatureStyleNode keys are relevant for each element type's display editor */
export type StyleKeyType = 'curve' | 'point' | 'line' | 'polygon' | 'label';

export interface DisplayStyleMeta {
  primaryKey: StyleKeyType;
  secondaryKeys?: StyleKeyType[];
  properties: readonly string[];
  hasLabel?: boolean;
  /** Which style key owns the label config. Defaults to primaryKey when omitted. */
  labelKey?: StyleKeyType;
}

const DISPLAY_STYLE_MAP: Record<string, DisplayStyleMeta> = {
  // Functions always use curve
  _function: { primaryKey: 'curve', properties: ['color', 'strokeWidth', 'dash'], hasLabel: true },

  // Point features
  roots: { primaryKey: 'point', properties: ['color', 'size', 'face'], hasLabel: true },
  extrema: { primaryKey: 'point', properties: ['color', 'size', 'face'], hasLabel: true },
  inflection: { primaryKey: 'point', properties: ['color', 'size', 'face'], hasLabel: true },
  yIntercept: { primaryKey: 'point', properties: ['color', 'size', 'face'], hasLabel: true },
  intersection: { primaryKey: 'point', properties: ['color', 'size', 'face'], hasLabel: true },
  handle: {
    primaryKey: 'point',
    properties: ['color', 'size', 'face', 'fixed', 'showGrabHandle'],
    hasLabel: true,
  },

  // Line features
  tangent: {
    primaryKey: 'line',
    secondaryKeys: ['point'],
    properties: ['color', 'strokeWidth'],
    hasLabel: true,
  },
  secant: {
    primaryKey: 'line',
    secondaryKeys: ['point'],
    properties: ['color', 'strokeWidth'],
    hasLabel: true,
  },
  axisOfSymmetry: { primaryKey: 'line', properties: ['color', 'strokeWidth', 'dash'], hasLabel: true },
  asymptotes: { primaryKey: 'line', properties: ['color', 'dash'] },

  // Curve features
  derivative: { primaryKey: 'curve', properties: ['color', 'strokeWidth', 'dash'], hasLabel: true },
  trajectory: { primaryKey: 'curve', properties: ['color'], hasLabel: true },
  monotonicity: { primaryKey: 'curve', properties: ['color'] },

  // Polygon features
  domain: {
    primaryKey: 'polygon',
    secondaryKeys: ['line'],
    properties: ['fillColor', 'fillOpacity', 'color', 'dash', 'strokeWidth'],
    hasLabel: true,
    labelKey: 'line',
  },
  range: {
    primaryKey: 'polygon',
    secondaryKeys: ['line'],
    properties: ['fillColor', 'fillOpacity', 'color', 'dash', 'strokeWidth'],
    hasLabel: true,
    labelKey: 'line',
  },
  functionArea: { primaryKey: 'polygon', properties: ['fillColor', 'fillOpacity', 'strokeColor', 'strokeWidth'] },
  intersectionArea: { primaryKey: 'polygon', properties: ['fillColor', 'fillOpacity', 'strokeColor', 'strokeWidth'] },

  // Periodic features
  amplitude: { primaryKey: 'line', secondaryKeys: ['point'], properties: ['color'] },
  wavelength: { primaryKey: 'line', secondaryKeys: ['point'], properties: ['color'] },
  period: { primaryKey: 'line', secondaryKeys: ['point'], properties: ['color'] },
  phaseShift: { primaryKey: 'line', secondaryKeys: ['point'], properties: ['color'] },

  // Primitives
  _primitive_point: {
    primaryKey: 'point',
    properties: ['color', 'size', 'face', 'fixed', 'strokeColor', 'fillOpacity', 'strokeWidth', 'showGrabHandle'],
    hasLabel: true,
  },
  _primitive_line: {
    primaryKey: 'line',
    properties: ['color', 'strokeWidth', 'dash', 'straightFirst', 'straightLast'],
    hasLabel: true,
  },
  _primitive_polygon: {
    primaryKey: 'polygon',
    properties: ['fillColor', 'fillOpacity', 'strokeColor', 'strokeWidth'],
  },
  _primitive_label: {
    primaryKey: 'label',
    properties: ['variant', 'color', 'position', 'fontSize', 'textAlign'],
  },
};

export function getDisplayStyleMeta(type: string, category: 'function' | 'feature' | 'primitive'): DisplayStyleMeta {
  if (category === 'function') {
    return DISPLAY_STYLE_MAP['_function'];
  }
  if (category === 'primitive') {
    return DISPLAY_STYLE_MAP[`_primitive_${type}`] ?? DISPLAY_STYLE_MAP['_function'];
  }
  return DISPLAY_STYLE_MAP[type] ?? DISPLAY_STYLE_MAP['_function'];
}

/** Determine which style key owns a given property. */
export function getStyleKeyForProperty(property: string, meta: DisplayStyleMeta): StyleKeyType {
  const allKeys = [meta.primaryKey, ...(meta.secondaryKeys ?? [])];
  // Polygon-owned properties
  if (['fillColor', 'fillOpacity', 'strokeColor'].includes(property) && allKeys.includes('polygon')) {
    return 'polygon';
  }
  // Line/curve-owned properties
  if (['color', 'dash', 'strokeWidth', 'straightFirst', 'straightLast'].includes(property)) {
    if (allKeys.includes('line')) {
      return 'line';
    }
    if (allKeys.includes('curve')) {
      return 'curve';
    }
  }
  // Point-owned properties
  if (['size', 'face', 'fixed', 'showGrabHandle'].includes(property) && allKeys.includes('point')) {
    return 'point';
  }
  return meta.primaryKey;
}

// =============================================================================
// GENERIC STYLE EDITOR SECTION DESCRIPTORS
// =============================================================================

// -- Default style values per element type (sourced from creator/model files) --

const FUNCTION_DEFAULT_COLORS: Record<string, string> = {
  linear: 'green',
  quadratic: 'sky',
  'quadratic-vertex': 'sky',
  cubic: 'indigo',
  'cubic-general': 'indigo',
  sine: 'pink',
  cosine: 'sky',
  exponential: 'purple',
  logarithmic: 'teal',
  rational: 'purple',
  'rational-general': 'sky',
  sqrt: 'sky',
  absolute: 'sky',
  'tangent-fn': 'sky',
};

const CURVE_DEFAULTS = { strokeWidth: 3, dash: 0 };
const POINT_DEFAULTS = { color: 'sky', size: 4 };
const LINE_DEFAULTS = { color: 'sky', strokeWidth: 2, dash: 0 };
const POLYGON_DEFAULTS = { fillColor: 'sky', fillOpacity: 0.2, strokeColor: 'sky', strokeWidth: 2 };

/** Feature style defaults extracted from each feature's model file */
const FEATURE_STYLE_DEFAULTS: Record<string, Record<string, Record<string, unknown>>> = {
  roots: { point: { color: 'teal' } },
  extrema: { point: {} },
  inflection: { point: { color: 'purple' } },
  yIntercept: { point: { color: 'green' } },
  intersection: { point: { color: 'green' } },
  handle: { point: { color: 'sky' } },
  tangent: { line: { color: 'amber', strokeWidth: 2 }, point: { color: 'amber' } },
  secant: { line: { color: 'pink', strokeWidth: 2 }, point: { color: 'pink' } },
  derivative: { curve: { color: 'amber', strokeWidth: 2, dash: 2 } },
  axisOfSymmetry: { line: { color: 'neutral', strokeWidth: 2, dash: 3 } },
  asymptotes: { line: { color: 'neutral', strokeWidth: 2, dash: 3 } },
  trajectory: { curve: { color: 'amber' } },
  monotonicity: { curve: { color: 'sky' } },
  domain: { polygon: { fillColor: 'sky-50', fillOpacity: 0.2 }, line: { color: 'sky-300', strokeWidth: 2, dash: 3 } },
  range: { polygon: { fillColor: 'sky-50', fillOpacity: 0.2 }, line: { color: 'sky-300', strokeWidth: 2, dash: 3 } },
  functionArea: { polygon: { fillColor: 'indigo', fillOpacity: 0.3, strokeWidth: 0 } },
  intersectionArea: { polygon: { fillColor: 'indigo', fillOpacity: 0.3, strokeWidth: 0 } },
  amplitude: { line: { color: 'sky', strokeWidth: 2 }, point: POINT_DEFAULTS },
  wavelength: { line: { color: 'teal', strokeWidth: 2 }, point: POINT_DEFAULTS },
  period: { line: { color: 'indigo', strokeWidth: 2 }, point: POINT_DEFAULTS },
  phaseShift: { line: { color: 'green', strokeWidth: 2 }, point: POINT_DEFAULTS },
};

const GRAPHPAD_STYLE_SECTIONS_MAP: Record<string, readonly StyleSectionDescriptor[]> = {
  // Functions (color is set dynamically in getGraphpadStyleSections)
  _function: [{ name: 'Curve', styleKey: 'curve', showLabel: true, defaults: { color: 'sky', ...CURVE_DEFAULTS } }],

  // Point features
  roots: [{ name: 'Points', styleKey: 'point', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['roots']?.['point'] }],
  extrema: [
    { name: 'Points', styleKey: 'point', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['extrema']?.['point'] },
    { name: 'Maxima', styleKey: 'point', subpartPath: ['maxima'], showLabel: true, defaults: { color: 'green' } },
    { name: 'Minima', styleKey: 'point', subpartPath: ['minima'], showLabel: true, defaults: { color: 'red' } },
  ],
  inflection: [
    { name: 'Points', styleKey: 'point', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['inflection']?.['point'] },
  ],
  yIntercept: [
    { name: 'Point', styleKey: 'point', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['yIntercept']?.['point'] },
  ],
  intersection: [
    { name: 'Points', styleKey: 'point', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['intersection']?.['point'] },
  ],
  handle: [
    { name: 'Point', styleKey: 'point', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['handle']?.['point'] },
  ],

  // Line features
  tangent: [
    { name: 'Line', styleKey: 'line', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['tangent']?.['line'] },
    { name: 'Touch Point', styleKey: 'point', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['tangent']?.['point'] },
  ],
  secant: [
    { name: 'Line', styleKey: 'line', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['secant']?.['line'] },
    {
      name: 'Start Point',
      styleKey: 'point',
      subpartPath: ['startPoint'],
      showLabel: true,
      defaults: FEATURE_STYLE_DEFAULTS['secant']?.['point'],
    },
    {
      name: 'End Point',
      styleKey: 'point',
      subpartPath: ['endPoint'],
      showLabel: true,
      defaults: FEATURE_STYLE_DEFAULTS['secant']?.['point'],
    },
    {
      name: 'Δx Line',
      styleKey: 'line',
      subpartPath: ['rateTriangle', 'horizontalLine'],
      showLabel: true,
      showVisible: true,
      defaults: { color: 'pink', strokeWidth: 2, dash: 2 },
    },
    {
      name: 'Δy Line',
      styleKey: 'line',
      subpartPath: ['rateTriangle', 'verticalLine'],
      showLabel: true,
      showVisible: true,
      defaults: { color: 'pink', strokeWidth: 2, dash: 2 },
    },
    {
      name: 'Corner',
      styleKey: 'point',
      subpartPath: ['rateTriangle', 'cornerPoint'],
      showLabel: true,
      showVisible: true,
      defaults: { color: 'pink' },
    },
  ],
  axisOfSymmetry: [
    { name: 'Line', styleKey: 'line', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['axisOfSymmetry']?.['line'] },
  ],
  asymptotes: [
    {
      name: 'Horizontal',
      styleKey: 'line',
      subpartPath: ['horizontal'],
      showLabel: true,
      defaults: FEATURE_STYLE_DEFAULTS['asymptotes']?.['line'],
    },
    {
      name: 'Vertical',
      styleKey: 'line',
      subpartPath: ['vertical'],
      showLabel: true,
      defaults: FEATURE_STYLE_DEFAULTS['asymptotes']?.['line'],
    },
  ],

  // Curve features
  derivative: [
    { name: 'Curve', styleKey: 'curve', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['derivative']?.['curve'] },
  ],
  trajectory: [
    { name: 'Curve', styleKey: 'curve', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['trajectory']?.['curve'] },
  ],
  monotonicity: [
    { name: 'Curve', styleKey: 'curve', defaults: FEATURE_STYLE_DEFAULTS['monotonicity']?.['curve'] },
    { name: 'Increasing', styleKey: 'polygon', subpartPath: ['increasing'], defaults: { fillColor: 'green' } },
    { name: 'Decreasing', styleKey: 'polygon', subpartPath: ['decreasing'], defaults: { fillColor: 'red' } },
  ],

  // Polygon features
  domain: [
    { name: 'Fill', styleKey: 'polygon', defaults: FEATURE_STYLE_DEFAULTS['domain']?.['polygon'] },
    { name: 'Boundary Lines', styleKey: 'line', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['domain']?.['line'] },
  ],
  range: [
    { name: 'Fill', styleKey: 'polygon', defaults: FEATURE_STYLE_DEFAULTS['range']?.['polygon'] },
    { name: 'Boundary Lines', styleKey: 'line', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['range']?.['line'] },
  ],
  functionArea: [
    { name: 'Area', styleKey: 'polygon', defaults: FEATURE_STYLE_DEFAULTS['functionArea']?.['polygon'] },
    { name: 'Positive', styleKey: 'polygon', subpartPath: ['positive'], defaults: { fillColor: 'green' } },
    { name: 'Negative', styleKey: 'polygon', subpartPath: ['negative'], defaults: { fillColor: 'red' } },
  ],
  intersectionArea: [
    { name: 'Area', styleKey: 'polygon', defaults: FEATURE_STYLE_DEFAULTS['intersectionArea']?.['polygon'] },
    { name: 'Positive', styleKey: 'polygon', subpartPath: ['positive'], defaults: { fillColor: 'green' } },
    { name: 'Negative', styleKey: 'polygon', subpartPath: ['negative'], defaults: { fillColor: 'red' } },
  ],

  // Periodic features
  amplitude: [
    { name: 'Line', styleKey: 'line', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['amplitude']?.['line'] },
    { name: 'Points', styleKey: 'point', showLabel: true, defaults: POINT_DEFAULTS },
    { name: 'Top', styleKey: 'line', subpartPath: ['top'], showLabel: true, showVisible: true, defaults: {} },
    { name: 'Bottom', styleKey: 'line', subpartPath: ['bottom'], showLabel: true, showVisible: true, defaults: {} },
    {
      name: 'Midline',
      styleKey: 'line',
      subpartPath: ['midline'],
      showLabel: true,
      showVisible: true,
      defaults: { color: 'amber', dash: 3 },
    },
    {
      name: 'Top Line',
      styleKey: 'line',
      subpartPath: ['topLine'],
      showLabel: true,
      showVisible: true,
      defaults: { dash: 3 },
    },
    {
      name: 'Bottom Line',
      styleKey: 'line',
      subpartPath: ['bottomLine'],
      showLabel: true,
      showVisible: true,
      defaults: { dash: 3 },
    },
  ],
  wavelength: [
    { name: 'Line', styleKey: 'line', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['wavelength']?.['line'] },
    { name: 'Points', styleKey: 'point', showLabel: true, defaults: POINT_DEFAULTS },
  ],
  period: [
    { name: 'Line', styleKey: 'line', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['period']?.['line'] },
    { name: 'Points', styleKey: 'point', showLabel: true, defaults: POINT_DEFAULTS },
    { name: 'Arrow', styleKey: 'line', subpartPath: ['arrow'], showLabel: true, defaults: { dash: 0 } },
    { name: 'Left Trace', styleKey: 'line', subpartPath: ['leftTrace'], showLabel: true, defaults: { dash: 3 } },
    { name: 'Right Trace', styleKey: 'line', subpartPath: ['rightTrace'], showLabel: true, defaults: { dash: 3 } },
  ],
  phaseShift: [
    { name: 'Line', styleKey: 'line', showLabel: true, defaults: FEATURE_STYLE_DEFAULTS['phaseShift']?.['line'] },
    { name: 'Points', styleKey: 'point', showLabel: true, defaults: POINT_DEFAULTS },
    {
      name: 'Reference Line',
      styleKey: 'line',
      subpartPath: ['referenceLine'],
      showLabel: true,
      showVisible: true,
      defaults: { dash: 3 },
    },
    {
      name: 'Arrow',
      styleKey: 'line',
      subpartPath: ['arrow'],
      showLabel: true,
      showVisible: true,
      defaults: { dash: 0 },
    },
  ],

  // Primitives
  _primitive_point: [{ name: 'Point', styleKey: 'point', showLabel: true, defaults: POINT_DEFAULTS }],
  _primitive_line: [{ name: 'Line', styleKey: 'line', showLabel: true, defaults: LINE_DEFAULTS }],
  _primitive_polygon: [
    { name: 'Fill', styleKey: 'polygon', defaults: POLYGON_DEFAULTS },
    { name: 'Border', styleKey: 'line', defaults: LINE_DEFAULTS },
  ],
  _primitive_label: [],
};

export function getGraphpadStyleSections(
  type: string,
  category: 'function' | 'feature' | 'primitive',
): readonly StyleSectionDescriptor[] {
  if (category === 'function') {
    const color = FUNCTION_DEFAULT_COLORS[type] ?? 'sky';
    return (GRAPHPAD_STYLE_SECTIONS_MAP['_function'] ?? []).map((s) => ({
      ...s,
      defaults: { ...s.defaults, color },
    }));
  }
  if (category === 'primitive') {
    return GRAPHPAD_STYLE_SECTIONS_MAP[`_primitive_${type}`] ?? [];
  }
  return GRAPHPAD_STYLE_SECTIONS_MAP[type] ?? [];
}

// =============================================================================
// PRIMITIVE HELPERS
// =============================================================================

export function getPrimitiveTypes(): PrimitiveType[] {
  return ['label', 'point', 'line', 'polygon'];
}

export function getPrimitiveDefaultParams(
  type: PrimitiveType,
): LabelElementParams | PointElementParams | LineElementParams | PolygonElementParams {
  switch (type) {
    case 'label':
      return { text: 'Label', position: { x: 0, y: 0 } };
    case 'point':
      return { x: 0, y: 0 };
    case 'line':
      return { from: { x: -1, y: 0 }, to: { x: 1, y: 0 } };
    case 'polygon':
      return {
        vertices: [
          { x: 0, y: 0 },
          { x: 1, y: 0 },
          { x: 0.5, y: 1 },
        ],
      };
  }
}

// =============================================================================
// OPTION CONSTANTS (re-exported from source definitions)
// =============================================================================

export const LABEL_POSITION_OPTIONS = LABEL_POSITIONS;
export const LABEL_VARIANT_OPTIONS = LABEL_VARIANTS;
export const LABEL_TEXT_ALIGN_OPTIONS = LABEL_TEXT_ALIGNS;
export const POINT_FACE_OPTIONS = POINT_FACES;

// =============================================================================
// COMPUTED VALUES PER ELEMENT TYPE
// =============================================================================

export interface ValueCollection {
  /** Collection name: '' for unnamed (roots), 'maxima'/'minima' for named (extrema) */
  name: string;
  /** Properties available per item, e.g. ['x', 'y'] */
  properties: readonly string[];
  /** Maximum items in the collection */
  maxItems: number;
}

export interface ElementValueInfo {
  params: string[];
  /** Scalar computed values only (no indexed/collection keys) */
  computed: string[];
  /** Indexed collections with structured access */
  collections: ValueCollection[];
}

/**
 * Returns the available value paths for a given element type.
 * For functions: input params from defaultParams + computed values like `expression`.
 * For features: computed values (with legacy indexed keys filtered out when collections exist).
 */
export function getElementValueInfo(type: string, category: 'function' | 'feature' | 'primitive'): ElementValueInfo {
  if (category === 'function') {
    const meta = getFunctionMeta(type);
    const functionComputed = [...FunctionRegistry.getComputedValueKeys(type), 'expression'];
    return { params: meta ? Object.keys(meta.defaultParams) : [], computed: functionComputed, collections: [] };
  }
  if (category === 'feature') {
    const defaults = getFeatureDefaultParams(type);
    const paramKeys = Object.keys(defaults).filter((k) => k !== 'of');
    const allComputed = [...FeatureRegistry.getComputedValueKeys(type)];
    const collectionDefs = FeatureRegistry.getComputedCollectionKeys(type);

    if (collectionDefs.length === 0) {
      return { params: paramKeys, computed: allComputed, collections: [] };
    }

    // Build collections and filter out legacy indexed keys
    const collections: ValueCollection[] = collectionDefs.map((c) => ({
      name: c.name,
      properties: c.properties,
      maxItems: 10,
    }));

    // Filter legacy indexed keys that correspond to collections
    const scalarComputed = allComputed.filter((key) => !isLegacyIndexedKey(key, collectionDefs));

    return { params: paramKeys, computed: scalarComputed, collections };
  }
  return { params: [], computed: [], collections: [] };
}

/**
 * Determines whether a computed value key is a legacy flat indexed key
 * that is superseded by a collection definition.
 *
 * For unnamed collections (name=''):  matches `x0`, `x1`, `y0`, etc. (letter + digit(s))
 * For named collections (name='maxima'): matches `maxima0X`, `maxima1Y`, etc. (name + digit(s) + optional uppercase suffix)
 */
function isLegacyIndexedKey(
  key: string,
  collections: ReadonlyArray<{ name: string; properties: readonly string[] }>,
): boolean {
  for (const col of collections) {
    if (col.name === '') {
      // Unnamed collection: match single-letter property + digits, e.g. x0, x1, y0
      if (/^[a-z]\d+$/.test(key)) {
        return true;
      }
    } else {
      // Named collection: match collectionName + digits + optional uppercase suffix
      // e.g. maxima0X, maxima0Y, minima6X, minima6Y
      const pattern = new RegExp(`^${col.name}\\d+[A-Z]?$`);
      if (pattern.test(key)) {
        return true;
      }
    }
  }
  return false;
}
